import {
  SimpleMarkerSymbolProperties,
  SimpleLineSymbolProperties,
  SimpleFillSymbolProperties,
} from '@arcgis/core/properties/defaultUnit';
import { applyTransparency, CYAN_COLOR } from './colorizer';

interface OpacityConfig {
  [key: string]: {
    [type: string]: {
      fillOpacity: number;
      strokeOpacity: number;
      width: number | string;
    }
  };
}

export enum GraphicStyle {
  BASIC_GEOMETRY = 'BASIC_GEOMETRY',
  TARGET_GEOMETRY = 'TARGET_GEOMETRY',
  NEARBY_GEOMETRIES = 'NEARBY_GEOMETRIES',
  PARENT_GEOMETRY = 'PARENT_GEOMETRY',
}

const opacityConfig: OpacityConfig = {
  [GraphicStyle.BASIC_GEOMETRY]: {
    point: { fillOpacity: 1, strokeOpacity: 1, width: 1 },
    linestring: { fillOpacity: 1, strokeOpacity: 1, width: '3px' },
    polygon: { fillOpacity: 0.3, strokeOpacity: 1, width: '3px' },
  },
  [GraphicStyle.TARGET_GEOMETRY]: {
    point: { fillOpacity: 1, strokeOpacity: 1, width: '2px' },
    linestring: { fillOpacity: 1, strokeOpacity: 1, width: '3px' },
    polygon: { fillOpacity: 0.1, strokeOpacity: 1, width: '3px' },
  },
  [GraphicStyle.NEARBY_GEOMETRIES]: {
    point: { fillOpacity: 0.5, strokeOpacity: 0.5, width: 0 },
    linestring: { fillOpacity: 0.5, strokeOpacity: 0.5, width: 2 },
    polygon: { fillOpacity: 0.2, strokeOpacity: 0.3, width: 2 },
  },
  [GraphicStyle.PARENT_GEOMETRY]: {
    point: { fillOpacity: 0.7, strokeOpacity: 0.5, width: 3 },
    linestring: { fillOpacity: 0.5, strokeOpacity: 0.5, width: '3px' },
    polygon: { fillOpacity: 0.3, strokeOpacity: 0.5, width: '3px' },
  },
};

export const applyOpacity = (color: string, geometryType: string, style?: GraphicStyle) => {
  const nearbyStrokeColor = style === GraphicStyle.TARGET_GEOMETRY ? CYAN_COLOR : color;
  const config = opacityConfig[style ?? GraphicStyle.BASIC_GEOMETRY][geometryType] || {};

  const { fillOpacity, strokeOpacity, width } = config;

  const fillColor = applyTransparency(color, fillOpacity);
  const strokeColor = applyTransparency(nearbyStrokeColor, strokeOpacity);

  return { fillColor, strokeColor, width };
};

export const pointStyles = (color: string, style?: GraphicStyle): SimpleMarkerSymbolProperties => {
  const { fillColor, strokeColor, width } = applyOpacity(color, 'point', style);

  return {
    style: 'circle',
    size: 10,
    color: fillColor,
    outline: { color: strokeColor, width },
  };
};

export const linestringStyles = (color: string, style?: GraphicStyle): SimpleLineSymbolProperties => {
  const { fillColor, width } = applyOpacity(color, 'linestring', style);

  return {
    color: fillColor,
    style: 'solid',
    width,
  };
};

export const polygonStyles = (color: string, style?: GraphicStyle): SimpleFillSymbolProperties => {
  const { fillColor, strokeColor, width } = applyOpacity(color, 'polygon', style);

  return {
    color: fillColor,
    style: 'solid',
    outline: {
      color: strokeColor,
      width,
    },
  };
};
